import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import IconSave from '../../components/icons/IconSave';
import language from '../../variables/language';
import wsManager from '../../services/wsManager';

const CLASSIFY_TOPIC = '/topic/word-cron/classify';
const DISCOVER_TOPIC = '/topic/word-cron/discover';

const BLANK = {
  classifyEnabled: true,
  classifyBatchSize: 30,
  classifyMaxBatchesPerRun: 3,
  classifyIntervalMinutes: 20,
  classifyCheckUnverified: true,
  classifyCheckMissingPartOfSpeech: true,
  classifyCheckMissingFrequency: true,
  classifyCheckMissingTense: true,
  classifyCheckMissingForm: false,
  classifyCheckMissingTranslation: true,
  discoveryEnabled: true,
  discoveryWordsPerRun: 15,
  discoveryIntervalHours: 6,
  discoverySourceMode: 'AI_GENERATED',
  discoveryPosMode: 'AUTO_ROTATE',
  discoveryFixedPartOfSpeechId: '',
  discoveryMinFrequency: '',
  discoveryMaxFrequency: '',
  botCaptureEnabled: false,
  botCaptureNewWords: false,
};

const flattenConfig = (data) => ({
  classifyEnabled: !!data.classifyEnabled,
  classifyBatchSize: data.classifyBatchSize,
  classifyMaxBatchesPerRun: data.classifyMaxBatchesPerRun,
  classifyIntervalMinutes: data.classifyIntervalMinutes,
  classifyCheckUnverified: !!data.classifyCheckUnverified,
  classifyCheckMissingPartOfSpeech: !!data.classifyCheckMissingPartOfSpeech,
  classifyCheckMissingFrequency: !!data.classifyCheckMissingFrequency,
  classifyCheckMissingTense: !!data.classifyCheckMissingTense,
  classifyCheckMissingForm: !!data.classifyCheckMissingForm,
  classifyCheckMissingTranslation: !!data.classifyCheckMissingTranslation,
  discoveryEnabled: !!data.discoveryEnabled,
  discoveryWordsPerRun: data.discoveryWordsPerRun,
  discoveryIntervalHours: data.discoveryIntervalHours,
  discoverySourceMode: data.discoverySourceMode || 'AI_GENERATED',
  discoveryPosMode: data.discoveryPosMode || 'AUTO_ROTATE',
  discoveryFixedPartOfSpeechId: data.discoveryFixedPartOfSpeech ? String(data.discoveryFixedPartOfSpeech.id) : '',
  discoveryMinFrequency: data.discoveryMinFrequency != null ? String(data.discoveryMinFrequency) : '',
  discoveryMaxFrequency: data.discoveryMaxFrequency != null ? String(data.discoveryMaxFrequency) : '',
  botCaptureEnabled: !!data.botCaptureEnabled,
  botCaptureNewWords: !!data.botCaptureNewWords,
});

function BatchProgress({ progress, t }) {
  if (!progress || !progress.active) return '';
  const batchNumbers = Object.keys(progress.batches).map(Number).sort((a, b) => a - b);
  if (batchNumbers.length === 0) return '';

  return (
    <div className="cron-config-progress">
      <span className="cron-config-progress-title">{t.progressTitle}</span>
      <div className="cron-config-progress-list">
        {batchNumbers.map((n) => {
          const b = progress.batches[n];
          return (
            <div className="cron-config-progress-row" key={n}>
              <span className="cron-config-progress-batch">
                {t.progressBatch} {n}{progress.maxBatches ? `/${progress.maxBatches}` : ''}
              </span>
              {b.status === 'running' ? (
                <span className="cron-config-progress-spinner" />
              ) : b.status === 'failed' ? (
                <span className="cron-config-progress-status cron-config-progress-failed">{t.progressFailed}</span>
              ) : (
                <span className="cron-config-progress-status cron-config-progress-done">
                  <b>{b.appliedCount}</b> {t.resultApplied}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TriggerResult({ result, t }) {
  const [expanded, setExpanded] = useState(false);
  if (!result) return '';
  const hasDetails = result.detailLines && result.detailLines.length > 0;

  const chips = [];
  if (result.pendingCount !== undefined) chips.push([t.resultPending, result.pendingCount]);
  if (result.batchesRun !== undefined) chips.push([t.resultBatches, result.batchesRun]);
  if (result.appliedCount !== undefined) chips.push([t.resultApplied, result.appliedCount]);
  if (result.requestedCount !== undefined) chips.push([t.resultRequested, result.requestedCount]);
  if (result.insertedCount !== undefined) chips.push([t.resultInserted, result.insertedCount]);

  return (
    <div className="cron-config-result">
      <div className="cron-config-result-chips">
        {chips.map(([label, value]) => (
          <span className="cron-config-chip" key={label}>
            <b>{value}</b> {label}
          </span>
        ))}
        {hasDetails ? (
          <span className="cron-config-expand-hint" onClick={() => setExpanded((v) => !v)}>
            {expanded ? '▲' : '▼'}
          </span>
        ) : (
          ''
        )}
      </div>
      {expanded && hasDetails ? <pre className="cron-config-details">{result.detailLines.join('\n')}</pre> : ''}
    </div>
  );
}

export default function CronConfigPanel() {
  const langRoot = language[language.lang];
  const t = langRoot.vocabularyConfigPage;

  const [formValues, setFormValues] = useState(BLANK);
  const [initialValues, setInitialValues] = useState(BLANK);
  const [partsOfSpeech, setPartsOfSpeech] = useState([]);
  const [saving, setSaving] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [classifyResult, setClassifyResult] = useState(null);
  const [discoverResult, setDiscoverResult] = useState(null);
  const [classifyProgress, setClassifyProgress] = useState(null);
  const [irregularVerbsStatus, setIrregularVerbsStatus] = useState(null);
  const [refreshingIrregularVerbs, setRefreshingIrregularVerbs] = useState(false);
  const [textSourceTopic, setTextSourceTopic] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [extractingFromText, setExtractingFromText] = useState(false);
  const [pasteResult, setPasteResult] = useState(null);
  const [platformCapture, setPlatformCapture] = useState({ captureEnabled: false, captureNewWords: false });
  const [platformCaptureInitial, setPlatformCaptureInitial] = useState({ captureEnabled: false, captureNewWords: false });
  const [platformCaptureSaving, setPlatformCaptureSaving] = useState(false);

  // Own entity, own endpoint (/api/platform-capture-config) - kept as its own
  // tiny fetch/save pair rather than folded into formValues/handleSave above,
  // since PlatformCaptureConfig is a separate singleton from WordCronConfig
  // (see the backend's own comment on why) and a combined dirty-flag would
  // make a PUT to one endpoint silently depend on the other's state.
  useEffect(() => {
    axios.get('/api/platform-capture-config', { withCredentials: true })
      .then((response) => {
        const flat = { captureEnabled: !!response.data.captureEnabled, captureNewWords: !!response.data.captureNewWords };
        setPlatformCapture(flat);
        setPlatformCaptureInitial(flat);
      })
      .catch(() => {});
  }, []);

  const platformCaptureDirty = JSON.stringify(platformCapture) !== JSON.stringify(platformCaptureInitial);

  const handlePlatformCaptureFieldChange = (name, value) => {
    setPlatformCapture((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlatformCaptureSave = () => {
    if (!platformCaptureDirty || platformCaptureSaving) return;
    setPlatformCaptureSaving(true);
    axios.put('/api/platform-capture-config', JSON.stringify(platformCapture), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }).then((response) => {
      const flat = { captureEnabled: !!response.data.captureEnabled, captureNewWords: !!response.data.captureNewWords };
      setPlatformCapture(flat);
      setPlatformCaptureInitial(flat);
    }).catch(() => {}).finally(() => setPlatformCaptureSaving(false));
  };

  // live batch-by-batch feedback while a classify run is in progress -
  // subscribed for the whole time the panel is mounted (not just around a
  // click) so a passive tab, or one navigated back to mid-run, also picks up
  // the live stream and the eventual final result. The HTTP trigger stays
  // fully synchronous and is still the source of truth for classifyResult/
  // discoverResult - these WS events are a supplementary real-time channel,
  // not a replacement, so a dropped WS message can never leave the button
  // stuck in a "running" state forever.
  useEffect(() => {
    const handleClassifyEvent = (event) => {
      const { type, payload } = event || {};
      if (type === 'run-start') {
        setClassifyProgress({ active: true, maxBatches: payload.maxBatches, batches: {} });
      } else if (type === 'batch-start') {
        setClassifyProgress((prev) => ({
          active: true,
          maxBatches: prev ? prev.maxBatches : null,
          batches: { ...(prev ? prev.batches : {}), [payload.batchNumber]: { status: 'running' } },
        }));
      } else if (type === 'batch-done') {
        setClassifyProgress((prev) => ({
          active: true,
          maxBatches: prev ? prev.maxBatches : null,
          batches: {
            ...(prev ? prev.batches : {}),
            [payload.batchNumber]: {
              status: payload.failed ? 'failed' : 'done',
              appliedCount: payload.appliedCount,
            },
          },
        }));
      } else if (type === 'run-complete') {
        setClassifyResult(payload);
        setClassifying(false);
        setClassifyProgress((prev) => (prev ? { ...prev, active: false } : prev));
      }
    };

    const handleDiscoverEvent = (event) => {
      const { type, payload } = event || {};
      if (type === 'run-complete') {
        setDiscoverResult(payload);
        setDiscovering(false);
      }
    };

    wsManager.subscribe(CLASSIFY_TOPIC, handleClassifyEvent);
    wsManager.subscribe(DISCOVER_TOPIC, handleDiscoverEvent);
    return () => {
      wsManager.unsubscribe(CLASSIFY_TOPIC, handleClassifyEvent);
      wsManager.unsubscribe(DISCOVER_TOPIC, handleDiscoverEvent);
    };
  }, []);

  useEffect(() => {
    axios.get('/api/word-cron-config', { withCredentials: true })
      .then((response) => {
        const flat = flattenConfig(response.data);
        setFormValues(flat);
        setInitialValues(flat);
      })
      .catch(() => {});
    axios.get('/api/partsofspeech', { withCredentials: true })
      .then((response) => setPartsOfSpeech(Array.isArray(response.data) ? response.data : []))
      .catch(() => {});
  }, []);

  // status/topic readouts are only fetched once the relevant source mode is
  // actually selected - no point hitting these on every page load when
  // AI_GENERATED (the default) doesn't use either.
  useEffect(() => {
    if (formValues.discoverySourceMode === 'IRREGULAR_VERBS') {
      axios.get('/api/word-cron/trigger/irregular-verbs-status', { withCredentials: true })
        .then((response) => setIrregularVerbsStatus(response.data))
        .catch(() => {});
    } else if (formValues.discoverySourceMode === 'TEXT_SOURCE') {
      axios.get('/api/word-cron/trigger/text-source-topic', { withCredentials: true })
        .then((response) => setTextSourceTopic(response.data.topic || ''))
        .catch(() => {});
    }
  }, [formValues.discoverySourceMode]);

  const isDirty = useMemo(() => JSON.stringify(formValues) !== JSON.stringify(initialValues), [formValues, initialValues]);

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };
  const handleDiscard = () => setFormValues(initialValues);

  const buildConfigPayload = () => ({
    classifyEnabled: formValues.classifyEnabled,
    classifyBatchSize: Number(formValues.classifyBatchSize),
    classifyMaxBatchesPerRun: Number(formValues.classifyMaxBatchesPerRun),
    classifyIntervalMinutes: Number(formValues.classifyIntervalMinutes),
    classifyCheckUnverified: formValues.classifyCheckUnverified,
    classifyCheckMissingPartOfSpeech: formValues.classifyCheckMissingPartOfSpeech,
    classifyCheckMissingFrequency: formValues.classifyCheckMissingFrequency,
    classifyCheckMissingTense: formValues.classifyCheckMissingTense,
    classifyCheckMissingForm: formValues.classifyCheckMissingForm,
    classifyCheckMissingTranslation: formValues.classifyCheckMissingTranslation,
    discoveryEnabled: formValues.discoveryEnabled,
    discoveryWordsPerRun: Number(formValues.discoveryWordsPerRun),
    discoveryIntervalHours: Number(formValues.discoveryIntervalHours),
    discoverySourceMode: formValues.discoverySourceMode,
    discoveryPosMode: formValues.discoveryPosMode,
    discoveryFixedPartOfSpeech: formValues.discoveryFixedPartOfSpeechId || null,
    discoveryMinFrequency: formValues.discoveryMinFrequency !== '' ? Number(formValues.discoveryMinFrequency) : null,
    discoveryMaxFrequency: formValues.discoveryMaxFrequency !== '' ? Number(formValues.discoveryMaxFrequency) : null,
    botCaptureEnabled: formValues.botCaptureEnabled,
    botCaptureNewWords: formValues.botCaptureNewWords,
  });

  const handleSave = () => {
    if (saving) return;
    setSaving(true);
    axios.put('/api/word-cron-config', JSON.stringify(buildConfigPayload()), {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    }).then((response) => {
      const flat = flattenConfig(response.data);
      setFormValues(flat);
      setInitialValues(flat);
    }).catch(() => {}).finally(() => setSaving(false));
  };

  const handleTriggerClassify = () => {
    if (classifying) return;
    setClassifying(true);
    setClassifyProgress({ active: true, maxBatches: null, batches: {} });
    axios.post('/api/word-cron/trigger/classify', {}, { withCredentials: true })
      .then((response) => setClassifyResult(response.data))
      .catch(() => {})
      .finally(() => setClassifying(false));
  };

  const handleTriggerDiscover = () => {
    if (discovering) return;
    setDiscovering(true);
    axios.post('/api/word-cron/trigger/discover', {}, { withCredentials: true })
      .then((response) => setDiscoverResult(response.data))
      .catch(() => {})
      .finally(() => setDiscovering(false));
  };

  const handleRefreshIrregularVerbs = () => {
    if (refreshingIrregularVerbs) return;
    setRefreshingIrregularVerbs(true);
    axios.post('/api/word-cron/trigger/refresh-irregular-verbs', {}, { withCredentials: true })
      .then(() => axios.get('/api/word-cron/trigger/irregular-verbs-status', { withCredentials: true }))
      .then((response) => setIrregularVerbsStatus(response.data))
      .catch(() => {})
      .finally(() => setRefreshingIrregularVerbs(false));
  };

  const handleExtractFromText = () => {
    if (extractingFromText || !pasteText.trim()) return;
    setExtractingFromText(true);
    axios.post('/api/word-cron/trigger/discover-from-text', { text: pasteText }, { withCredentials: true })
      .then((response) => setPasteResult(response.data))
      .catch(() => {})
      .finally(() => setExtractingFromText(false));
  };

  return (
    <div className="vocab-config-tab-body">
      <div className="cron-config-grid">
        <div className="cron-config-card">
          <label className="cron-config-toggle-row">
            <input type="checkbox" checked={formValues.classifyEnabled} onChange={(e) => handleFieldChange('classifyEnabled', e.target.checked)} />
            <span>{t.classifyEnabled}</span>
          </label>
          <label className="cron-config-field">
            <span>{t.classifyBatchSize}</span>
            <input type="number" min="1" value={formValues.classifyBatchSize} onChange={(e) => handleFieldChange('classifyBatchSize', e.target.value)} />
          </label>
          <label className="cron-config-field">
            <span>{t.classifyMaxBatchesPerRun}</span>
            <input type="number" min="1" value={formValues.classifyMaxBatchesPerRun} onChange={(e) => handleFieldChange('classifyMaxBatchesPerRun', e.target.value)} />
          </label>
          <p className="cron-config-hint">{t.classifyBatchHint}</p>
          <label className="cron-config-field">
            <span>{t.classifyIntervalMinutes}</span>
            <input type="number" min="1" value={formValues.classifyIntervalMinutes} onChange={(e) => handleFieldChange('classifyIntervalMinutes', e.target.value)} />
          </label>
          <div className="cron-config-conditions">
            <span className="cron-config-conditions-title">{t.classifyConditionsTitle}</span>
            <label className="cron-config-condition-row">
              <input type="checkbox" checked={formValues.classifyCheckUnverified} onChange={(e) => handleFieldChange('classifyCheckUnverified', e.target.checked)} />
              <span>{t.classifyCheckUnverified}</span>
            </label>
            <label className="cron-config-condition-row">
              <input type="checkbox" checked={formValues.classifyCheckMissingPartOfSpeech} onChange={(e) => handleFieldChange('classifyCheckMissingPartOfSpeech', e.target.checked)} />
              <span>{t.classifyCheckMissingPartOfSpeech}</span>
            </label>
            <label className="cron-config-condition-row">
              <input type="checkbox" checked={formValues.classifyCheckMissingFrequency} onChange={(e) => handleFieldChange('classifyCheckMissingFrequency', e.target.checked)} />
              <span>{t.classifyCheckMissingFrequency}</span>
            </label>
            <label className="cron-config-condition-row">
              <input type="checkbox" checked={formValues.classifyCheckMissingTense} onChange={(e) => handleFieldChange('classifyCheckMissingTense', e.target.checked)} />
              <span>{t.classifyCheckMissingTense}</span>
            </label>
            <label className="cron-config-condition-row">
              <input type="checkbox" checked={formValues.classifyCheckMissingForm} onChange={(e) => handleFieldChange('classifyCheckMissingForm', e.target.checked)} />
              <span>{t.classifyCheckMissingForm}</span>
            </label>
            <label className="cron-config-condition-row">
              <input type="checkbox" checked={formValues.classifyCheckMissingTranslation} onChange={(e) => handleFieldChange('classifyCheckMissingTranslation', e.target.checked)} />
              <span>{t.classifyCheckMissingTranslation}</span>
            </label>
          </div>
          <button type="button" className="cron-config-trigger-btn" disabled={classifying} onClick={handleTriggerClassify}>
            {classifying ? t.triggering : t.triggerClassify}
          </button>
          <BatchProgress progress={classifyProgress} t={t} />
          <TriggerResult result={classifyResult} t={t} />
        </div>

        <div className="cron-config-card">
          <label className="cron-config-toggle-row">
            <input type="checkbox" checked={formValues.discoveryEnabled} onChange={(e) => handleFieldChange('discoveryEnabled', e.target.checked)} />
            <span>{t.discoveryEnabled}</span>
          </label>
          <label className="cron-config-field">
            <span>{t.discoveryWordsPerRun}</span>
            <input type="number" min="1" value={formValues.discoveryWordsPerRun} onChange={(e) => handleFieldChange('discoveryWordsPerRun', e.target.value)} />
          </label>
          <label className="cron-config-field">
            <span>{t.discoveryIntervalHours}</span>
            <input type="number" min="1" value={formValues.discoveryIntervalHours} onChange={(e) => handleFieldChange('discoveryIntervalHours', e.target.value)} />
          </label>
          <label className="cron-config-field">
            <span>{t.discoverySourceMode}</span>
            <select value={formValues.discoverySourceMode} onChange={(e) => handleFieldChange('discoverySourceMode', e.target.value)}>
              <option value="AI_GENERATED">{t.sourceModeAiGenerated}</option>
              <option value="IRREGULAR_VERBS">{t.sourceModeIrregularVerbs}</option>
              <option value="TEXT_SOURCE">{t.sourceModeTextSource}</option>
            </select>
          </label>

          {formValues.discoverySourceMode === 'AI_GENERATED' ? (
            <>
              <label className="cron-config-field">
                <span>{t.discoveryPosMode}</span>
                <select value={formValues.discoveryPosMode} onChange={(e) => handleFieldChange('discoveryPosMode', e.target.value)}>
                  <option value="AUTO_ROTATE">{t.posModeAuto}</option>
                  <option value="FIXED">{t.posModeFixed}</option>
                </select>
              </label>
              {formValues.discoveryPosMode === 'FIXED' ? (
                <label className="cron-config-field">
                  <span>{t.discoveryFixedPartOfSpeech}</span>
                  <select value={formValues.discoveryFixedPartOfSpeechId} onChange={(e) => handleFieldChange('discoveryFixedPartOfSpeechId', e.target.value)}>
                    <option value="">{langRoot['not selected']}</option>
                    {partsOfSpeech.map((p) => <option key={p.id} value={p.id}>{p.description}</option>)}
                  </select>
                </label>
              ) : (
                ''
              )}
              <label className="cron-config-field">
                <span>{t.discoveryMinFrequency}</span>
                <input type="number" step="0.1" value={formValues.discoveryMinFrequency} onChange={(e) => handleFieldChange('discoveryMinFrequency', e.target.value)} />
              </label>
              <label className="cron-config-field">
                <span>{t.discoveryMaxFrequency}</span>
                <input type="number" step="0.1" value={formValues.discoveryMaxFrequency} onChange={(e) => handleFieldChange('discoveryMaxFrequency', e.target.value)} />
              </label>
            </>
          ) : ''}

          {formValues.discoverySourceMode === 'IRREGULAR_VERBS' ? (
            <div className="cron-config-source-info">
              {irregularVerbsStatus ? (
                <p className="cron-config-hint">
                  {t.irregularVerbsStatus
                    .replace('{covered}', irregularVerbsStatus.totalForms - irregularVerbsStatus.missingForms)
                    .replace('{total}', irregularVerbsStatus.totalForms)}
                </p>
              ) : (
                <p className="cron-config-hint">{t.irregularVerbsNoReference}</p>
              )}
              <button type="button" className="cron-config-trigger-btn secondary" disabled={refreshingIrregularVerbs} onClick={handleRefreshIrregularVerbs}>
                {refreshingIrregularVerbs ? t.triggering : t.refreshIrregularVerbs}
              </button>
            </div>
          ) : ''}

          {formValues.discoverySourceMode === 'TEXT_SOURCE' ? (
            <div className="cron-config-source-info">
              <p className="cron-config-hint">{t.textSourceTodayTopic}: <b>{textSourceTopic || '…'}</b></p>
            </div>
          ) : ''}

          <button type="button" className="cron-config-trigger-btn" disabled={discovering} onClick={handleTriggerDiscover}>
            {discovering ? t.triggering : t.triggerDiscover}
          </button>
          <TriggerResult result={discoverResult} t={t} />

          {formValues.discoverySourceMode === 'TEXT_SOURCE' ? (
            <div className="cron-config-paste-text">
              <span className="cron-config-conditions-title">{t.pasteTextTitle}</span>
              <textarea className="cron-config-paste-textarea" rows={5} placeholder={t.pasteTextPlaceholder}
                value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
              <button type="button" className="cron-config-trigger-btn secondary" disabled={extractingFromText || !pasteText.trim()} onClick={handleExtractFromText}>
                {extractingFromText ? t.triggering : t.pasteTextButton}
              </button>
              <TriggerResult result={pasteResult} t={t} />
            </div>
          ) : ''}
        </div>

        <div className="cron-config-card">
          <span className="cron-config-conditions-title">{t.botCaptureTitle}</span>
          <label className="cron-config-toggle-row">
            <input id="bot-capture-enabled-checkbox" type="checkbox" checked={formValues.botCaptureEnabled} onChange={(e) => handleFieldChange('botCaptureEnabled', e.target.checked)} />
            <span>{t.botCaptureEnabled}</span>
          </label>
          <p className="cron-config-hint">{t.botCaptureDescription}</p>
          <label className="cron-config-toggle-row" style={{ opacity: formValues.botCaptureEnabled ? 1 : 0.5 }}>
            <input id="bot-capture-new-words-checkbox" type="checkbox" checked={formValues.botCaptureNewWords} disabled={!formValues.botCaptureEnabled}
              onChange={(e) => handleFieldChange('botCaptureNewWords', e.target.checked)} />
            <span>{t.botCaptureNewWords}</span>
          </label>
          <p className="cron-config-hint">{t.botCaptureNewWordsDescription}</p>
        </div>

        <div className="cron-config-card">
          <span className="cron-config-conditions-title">{t.platformCaptureTitle}</span>
          <label className="cron-config-toggle-row">
            <input id="platform-capture-enabled-checkbox" type="checkbox" checked={platformCapture.captureEnabled}
              onChange={(e) => handlePlatformCaptureFieldChange('captureEnabled', e.target.checked)} />
            <span>{t.platformCaptureEnabled}</span>
          </label>
          <p className="cron-config-hint">{t.platformCaptureDescription}</p>
          <label className="cron-config-toggle-row" style={{ opacity: platformCapture.captureEnabled ? 1 : 0.5 }}>
            <input id="platform-capture-new-words-checkbox" type="checkbox" checked={platformCapture.captureNewWords} disabled={!platformCapture.captureEnabled}
              onChange={(e) => handlePlatformCaptureFieldChange('captureNewWords', e.target.checked)} />
            <span>{t.platformCaptureNewWords}</span>
          </label>
          <p className="cron-config-hint">{t.platformCaptureNewWordsDescription}</p>
          {platformCaptureDirty ? (
            <div className="cron-config-form-footer">
              <span className="cron-config-unsaved-hint">{langRoot.unsavedChanges}</span>
              <button type="button" className="cron-config-save-btn" disabled={platformCaptureSaving} onClick={handlePlatformCaptureSave}>
                <IconSave />
                {platformCaptureSaving ? langRoot['Loading ...'] : langRoot.save}
              </button>
            </div>
          ) : ''}
        </div>
      </div>

      {isDirty ? (
        <div className="cron-config-form-footer">
          <span className="cron-config-unsaved-hint">{langRoot.unsavedChanges}</span>
          <div className="cron-config-discard-link" onClick={handleDiscard}>{langRoot.cancel}</div>
          <button type="button" className="cron-config-save-btn" disabled={saving} onClick={handleSave}>
            <IconSave />
            {saving ? langRoot['Loading ...'] : langRoot.save}
          </button>
        </div>
      ) : (
        ''
      )}
    </div>
  );
}
